import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {isAxiosError} from 'axios'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'
import type {components} from '@/lib/api-schema'

export type PriceBookEntry = components['schemas']['PriceBookEntryRead']
export type PriceGrid = components['schemas']['PriceGridRead']
export type PriceBookItemKind = components['schemas']['PriceBookItemKind']
export type BillingCycle = components['schemas']['BillingCycle']
export type AdminInvoice = components['schemas']['AdminBillingInvoiceRead']
export type AdminSubscription = components['schemas']['AdminBillingSubscriptionRead']
export type BillingInvoiceEstado = components['schemas']['BillingInvoiceEstado']
export type BillingSubscriptionEstado = components['schemas']['BillingSubscriptionEstado']
export type BillingPayerKind = components['schemas']['BillingPayerKind']
export type CarteraResumen = components['schemas']['CarteraResumenRead']
export type PriceResolution = components['schemas']['PriceResolutionRead']

export const CARTERA_PAGE_SIZE = 20

export const INVOICE_ESTADO_TONE: Record<BillingInvoiceEstado, 'green' | 'amber' | 'red' | 'neutral'> = {
  emitida: 'neutral',
  pagada: 'green',
  vencida: 'amber',
  anulada: 'neutral',
}

export const SUBSCRIPTION_ESTADO_TONE: Record<BillingSubscriptionEstado, 'green' | 'amber' | 'red' | 'neutral'> = {
  activa: 'green',
  pendiente_pago: 'neutral',
  en_gracia: 'amber',
  degradada: 'red',
  cancelada: 'neutral',
}

// Espejo de SIZE_BANDS × RISK_GROUPS de `backend/app/common/segmentacion.py`.
export const SIZE_BANDS = ['1-10', '11-50', '51-100', '101-200', '201+'] as const
export const RISK_GROUPS = ['I-III', 'IV-V'] as const
export const ALL_SEGMENT_KEYS: string[] = SIZE_BANDS.flatMap((size) => RISK_GROUPS.map((risk) => `${size}|${risk}`))

export function usePriceGrid(enabled: boolean) {
  return useQuery({
    queryKey: ['cartera-precios-rejilla'],
    queryFn: async () => (await api.get<PriceGrid>('/admin/cartera/precios-rejilla')).data,
    enabled,
  })
}

export function useSetPrice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      item_kind: PriceBookItemKind
      segment_key: string
      billing_cycle: BillingCycle
      amount_cop: number
      iva_pct?: number
    }) => (await api.post<PriceBookEntry>('/admin/cartera/precios', input)).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['cartera-precios-rejilla']}),
  })
}

export function useSetPriceOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      item_kind: PriceBookItemKind
      segment_key: string | null
      billing_cycle: BillingCycle
      amount_cop: number
      iva_pct?: number
      business_id?: string
      partner_id?: string
    }) => (await api.post<PriceBookEntry>('/admin/cartera/precios', input)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['cartera-precios-rejilla']})
      queryClient.invalidateQueries({queryKey: ['cartera-precios-resolver']})
      queryClient.invalidateQueries({queryKey: ['cartera-precios-scoped']})
    },
  })
}

// DELETE retira la fila vigente sin sucesora: el precio cae al siguiente peldaño (partner o base).
export function useDeletePriceEntry() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entryId: string) => api.delete(`/admin/cartera/precios/${entryId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['cartera-precios-rejilla']})
      queryClient.invalidateQueries({queryKey: ['cartera-precios-resolver']})
      queryClient.invalidateQueries({queryKey: ['cartera-precios-scoped']})
    },
  })
}

// La misma escalera que lee una factura real; 404 (ningún peldaño con precio) es `null`.
export function useResolverPrecio(params: {
  segmentKey: string | null
  billingCycle: BillingCycle
  businessId?: string
  partnerId?: string
  itemKind?: PriceBookItemKind
  enabled: boolean
}) {
  const itemKind = params.itemKind ?? 'saas'
  return useQuery({
    queryKey: [
      'cartera-precios-resolver',
      itemKind,
      params.segmentKey,
      params.billingCycle,
      params.businessId ?? null,
      params.partnerId ?? null,
    ],
    queryFn: async () => {
      try {
        return (
          await api.get<PriceResolution>('/admin/cartera/precios-resolver', {
            params: {
              item_kind: itemKind,
              segment_key: params.segmentKey,
              billing_cycle: params.billingCycle,
              business_id: params.businessId,
              partner_id: params.partnerId,
            },
          })
        ).data
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null
        throw err
      }
    },
    enabled: params.enabled && !!params.segmentKey,
  })
}

export function useCarteraResumen(enabled: boolean) {
  return useQuery({
    queryKey: ['cartera-resumen'],
    queryFn: async () => (await api.get<CarteraResumen>('/admin/cartera/resumen')).data,
    enabled,
  })
}

export type AutoChargeAttempt = components['schemas']['AutoChargeAttemptRead']

export function useAutoChargeAttempts(businessId: string, page = 1, enabled = true) {
  return useQuery({
    queryKey: ['cartera-intentos-cargo', businessId, page],
    queryFn: async () =>
      (
        await api.get<Page<AutoChargeAttempt>>(`/admin/cartera/empresas/${businessId}/intentos-cargo`, {
          params: {page, page_size: CARTERA_PAGE_SIZE},
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useCarteraFacturas(
  params: {
    page: number
    search: string
    estado: BillingInvoiceEstado | ''
    payerKind: BillingPayerKind | ''
    businessId?: string
    partnerId?: string
  },
  enabled = true
) {
  return useQuery({
    queryKey: ['cartera-facturas', params],
    queryFn: async () =>
      (
        await api.get<Page<AdminInvoice>>('/admin/cartera/facturas', {
          params: {
            page: params.page,
            page_size: CARTERA_PAGE_SIZE,
            search: params.search || undefined,
            estado: params.estado || undefined,
            payer_kind: params.payerKind || undefined,
            business_id: params.businessId,
            partner_id: params.partnerId,
          },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

export function useCarteraSuscripciones(
  params: {
    page: number
    search: string
    estado: BillingSubscriptionEstado | ''
    payerKind: BillingPayerKind | ''
    partnerId?: string
  },
  enabled = true
) {
  return useQuery({
    queryKey: ['cartera-suscripciones', params],
    queryFn: async () =>
      (
        await api.get<Page<AdminSubscription>>('/admin/cartera/suscripciones', {
          params: {
            page: params.page,
            page_size: CARTERA_PAGE_SIZE,
            search: params.search || undefined,
            estado: params.estado || undefined,
            payer_kind: params.payerKind || undefined,
            partner_id: params.partnerId,
          },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

function useCarteraInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['cartera-facturas']})
    queryClient.invalidateQueries({queryKey: ['cartera-suscripciones']})
    queryClient.invalidateQueries({queryKey: ['cartera-resumen']})
  }
}

export function useAnularFactura() {
  const invalidate = useCarteraInvalidation()
  return useMutation({
    mutationFn: async ({invoiceId, motivo}: {invoiceId: string; motivo: string}) =>
      (await api.post<AdminInvoice>(`/admin/cartera/facturas/${invoiceId}/anular`, {motivo})).data,
    onSuccess: invalidate,
  })
}

export function useRegistrarPagoManual() {
  const invalidate = useCarteraInvalidation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      invoiceId,
      referenciaExterna,
      metodo,
    }: {
      invoiceId: string
      referenciaExterna: string
      metodo: string
    }) =>
      (
        await api.post<AdminInvoice>(`/admin/cartera/facturas/${invoiceId}/pago-manual`, {
          referencia_externa: referenciaExterna,
          metodo,
        })
      ).data,
    onSuccess: (data) => {
      invalidate()
      // El panel de la empresa lee su plan de `['billing', businessId]`, no de esta lista.
      queryClient.invalidateQueries({queryKey: ['billing', data.business_id]})
    },
  })
}

export function useToggleSst() {
  const invalidate = useCarteraInvalidation()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({subscriptionId, incluir}: {subscriptionId: string; incluir: boolean}) =>
      (
        await api.post<AdminSubscription>(`/admin/cartera/suscripciones/${subscriptionId}/sst`, {
          incluir,
        })
      ).data,
    onSuccess: (data) => {
      invalidate()
      // El switch del panel lee `subscription.items` de `['billing', businessId]`.
      queryClient.invalidateQueries({queryKey: ['billing', data.business_id]})
    },
  })
}

// Lo vigente lo filtra el servidor: filtrar después de paginar deja páginas a medias y un total falso.
export function useScopedPriceBook(
  scope: {businessId?: string; partnerId?: string},
  page = 1,
  itemKind?: PriceBookItemKind
) {
  const businessId = scope.businessId ?? null
  const partnerId = scope.partnerId ?? null
  return useQuery({
    queryKey: ['cartera-precios-scoped', businessId, partnerId, page, itemKind ?? null],
    queryFn: async () =>
      (
        await api.get<Page<PriceBookEntry>>('/admin/cartera/precios', {
          params: {
            page,
            page_size: CARTERA_PAGE_SIZE,
            vigente: true,
            item_kind: itemKind,
            business_id: businessId ?? undefined,
            partner_id: partnerId ?? undefined,
          },
        })
      ).data,
    enabled: !!businessId || !!partnerId,
    placeholderData: (previous) => previous,
  })
}
